/**
 * @description Centralized API endpoint constants
 * All API endpoints should be defined here for better maintainability
 */

import { ENV } from '../config/env';

const APIConstants = {
  // Authentication endpoints
  AUTH: {
    LOGIN: 'auth/login',
    LOGOUT: 'auth/logout',
    REFRESH: 'auth/refresh',
    REGISTER: 'auth/login',
    GITHUB_LOGOUT: 'auth/github/logout',
    BITBUCKET_LOGOUT: 'auth/bitbucket/logout',
  },

  // Credential management endpoints
  CREDENTIALS: {
    STORE: 'credential/store-creds',
    GET: 'credential/get-creds',
    GET_ACCOUNT_IDS: 'credential/get-account-ids',
    DELETE: 'credential/delete-creds',
    STORE_GITHUB: 'credential/store-github-creds',
    GET_GITHUB_LIST: 'credential/get-github-creds-list',
    // Azure
    STORE_AZURE: 'credential/store-azure-creds',
    GET_AZURE: 'credential/get-azure-creds',
    GET_AZURE_TENANTS: 'credential/get-azure-tenant-ids',
    DELETE_AZURE: 'credential/delete-azure-creds',
  },

  // Repository endpoints
  REPOSITORY: {
    SCAN: 'repo/scan',
    SCAN_UPLOAD_ZIP: 'repo/scan/upload-zip',
    SCAN_LIST_NAMES: 'repo/scan-list-names',
    SCAN_LIST: 'repo/scan-list',
    OPENGREP: 'repo/opengrep',
    REPOS: 'repo/repos',
    BITBUCKET_REPOS: 'repo/bitbucket/repos',
    BRANCHES: 'repo/branches',
    BITBUCKET_BRANCHES: 'repo/bitbucket/branches',
  },

  // Report endpoints
  REPORTS: {
    SOURCES: 'report/sources',
    GET_REPORT: 'report/get',
    DELETE_REPORT: 'report/delete',
  },

  // User management endpoints
  USER: {
    PROFILE: 'user/profile',
    SETTINGS: 'user/settings',
    PREFERENCES: 'user/preferences',
  },

  // User Email endpoints
  USER_EMAIL: {
    GET_CONFIG: 'user-email/config',
    STORE_CONFIG: 'user-email/config',
    UPDATE_CONFIG: 'user-email/config',
    DELETE_CONFIG: 'user-email/config',
    INITIALIZE_INDEX: 'user-email/initialize-index',
  },

  // Notification endpoints
  NOTIFICATIONS: {
    SETTINGS: 'notification/settings',
    GET_NOTIFICATIONS: 'notification/list',
    MARK_READ: 'notification/mark-read',
  },

  // File upload endpoints
  UPLOAD: {
    SINGLE: 'upload/single',
    MULTIPLE: 'upload/multiple',
    ZIP: 'upload/zip',
  },

  // Health check endpoints
  HEALTH: {
    CHECK: 'health/check',
    STATUS: 'health/status',
  },

  // GitHub OAuth org-level configuration
  GITHUB_OAUTH: {
    CONFIG: 'oauth/github/config',
  },

  // Bitbucket OAuth org-level configuration
  BITBUCKET_OAUTH: {
    CONFIG: 'oauth/bitbucket/config',
  },

  // Slack OAuth org-level configuration
  SLACK_OAUTH: {
    CONFIG: 'oauth/slack/config',
  },

  // AWS ALB endpoints
  AWS_ALB: {
    GET_ALBS: 'aws/albs',
    DEREGISTER_TARGET: 'aws/deregister-target',
    GET_UNHEALTHY_DETAILS: 'aws/unhealthy-target-details',
    TERMINATE_INSTANCE: 'aws/terminate-instance',

    // CloudWatch endpoints
    FETCH_REALTIME: 'alb/fetch-realtime',
    METRICS: 'alb/metrics',
    TARGET_HEALTH: 'alb/target-health',
    ACCESS_LOGS: 'alb/access-logs',
    DASHBOARD: 'alb/dashboard',

    // Memory storage endpoints
    INITIALIZE_MEMORY: 'alb/initialize-memory',
    FETCH_MEMORY: 'alb/fetch-memory',
    DATA_MEMORY: 'alb/data-memory',
    STATISTICS_MEMORY: 'alb/statistics-memory',
    START_AUTO_REFRESH: 'alb/auto-refresh/start-memory',
    STOP_AUTO_REFRESH: 'alb/auto-refresh/stop-memory',
    REFRESH_STATUS: 'alb/auto-refresh/status-memory',
    FORCE_REFRESH: 'alb/force-refresh-memory',
    GLOBAL_REFRESH_STATS: 'alb/global-refresh-stats',
  },

  // ALB Account Settings endpoints
  ALB_ACCOUNT_SETTINGS: {
    GET_SETTINGS: 'alb-account-settings/settings',
    UPDATE_SETTINGS: 'alb-account-settings/settings',
    GET_BATCH_SETTINGS: 'alb-account-settings/batch-settings',
    LOG_ACTION: 'alb-account-settings/actions/log',
    GET_USERS_WITH_AUTO_DEREGISTER: 'alb-account-settings/accounts/auto-deregister-enabled',
    BULK_DISABLE_AUTO_DEREGISTER: 'alb-account-settings/bulk-disable-auto-deregister',
    GET_STATS: 'alb-account-settings/actions/stats',
    GET_FAILED_ACTIONS: 'alb-account-settings/actions/failed',
  },

  // Auto Deregister Activity endpoints
  AUTO_DEREGISTER_ACTIVITY: {
    LATEST: 'alb/auto-deregister-activity/latest/:accountId',
  },

  // Health Monitor endpoints
  HEALTH_MONITOR: {
    STATUS: 'health-monitor/status',
    START: 'health-monitor/start',
    STOP: 'health-monitor/stop',
    CHECK: 'health-monitor/check',
    NOTIFY_UNHEALTHY: 'health-monitor/notify-unhealthy',
    ADD_REGION: 'health-monitor/region/add',
    REMOVE_REGION: 'health-monitor/region/remove',
    GET_EMAIL_CONFIG: 'health-monitor/email-config',
    UPDATE_EMAIL_CONFIG: 'health-monitor/email-config',
    TEST_HEALTHY_EMAIL: 'health-monitor/test-healthy-email',
    TEST_UNHEALTHY_EMAIL: 'health-monitor/test-unhealthy-email',
    TEST_EMAIL: 'health-monitor/test-email',
    TEST_SES_CONNECTIVITY: 'health-monitor/test-ses-connectivity',
  },

  // Slack Integration endpoints
  SLACK: {
    DISCONNECT: 'slack/disconnect',
    TEST: 'slack/test',
  },

  // User Target Selections endpoints
  USER_TARGET_SELECTIONS: {
    GET: 'user/target-selections',
    SAVE: 'user/target-selections',
    EXCLUDE_TARGET: 'user/exclude-target',
    REMOVE_EXCLUSION: 'user/remove-exclusion',
    GET_EXCLUDED_TARGETS: 'user/excluded-targets',
    AUTO_DEREGISTER: 'user/auto-deregister',
  },

  // ALB Auto Actions endpoints
  ALB_AUTO_ACTIONS: {
    START_DEREGISTER: 'alb/auto-actions/deregister/start',
    STOP_DEREGISTER: 'alb/auto-actions/deregister/stop',
    GET_DEREGISTER_STATE: 'alb/auto-actions/deregister/state/:accountId',
    PERFORM_DEREGISTER: 'alb/auto-actions/deregister/perform',
    RUN_ON_DEMAND_DEREGISTER: 'alb/auto-actions/deregister/run-on-demand',
    GET_ACTIVE_ACTIONS: 'alb/auto-actions/active',
  },
};

export default APIConstants;
